import { useState } from 'react'

import { getRequestEmailThreads } from '../../../../../api'
import { FILE_SCOPE_REQUEST } from '../../../../../constants/file'
import { REQUEST_PROFILE_CHANGE_TYPE } from '../../../../../constants/request'
import { useNotification } from '../../../../../hooks'
import { EmailAttachedThreadsModal } from '../../../../Email'
import { FileAttachedItemsModal } from '../../../../File'
import {
  getPipelinePhaseFiles,
  populatePipelinePhase,
  togglePipelinePhaseVisibilityToTenant,
} from '../../../../Pipeline'
import {
  getAttachedEmailThreads,
  getEmailThreadsFromResponse,
} from '../../../../Task'
import { REQUEST_PROFILE_TAB_EMAILS } from '../../../RequestProfile.constants'
import { useRequestProfileContext } from '../../../RequestProfile.hooks'
import { RequestProfileTabEmailsThreadModal } from '../../../RequestProfileTabEmails'
import {
  getActivityVisibilityChangeNotificationKey,
  getPhaseFilesApiProps,
  getPhaseFilesFromResponse,
  getPhaseFilesPaginationData,
} from '../../RequestProfileTabActivities.utils'
import { useAddActivityModal } from '../useAddActivityModal'
import { useRequestProfileTabActivitiesContext } from '../useRequestProfileTabActivitiesContext'

export function useRequestProfileTabActivitiesPhaseActions(phase) {
  const { closeModal, handleReload, requestId, setModal } =
    useRequestProfileTabActivitiesContext()

  const {
    handleLoadCounters,
    handleReloadAfterChange,
    handleReloadOpenActivities,
    outboundUuid,
  } = useRequestProfileContext()

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const handleAddActivity = useAddActivityModal({ phase })

  const [isStartPhaseLoading, setStartPhaseLoading] = useState(false)

  async function handleStartPhase() {
    setStartPhaseLoading(true)

    try {
      await populatePipelinePhase(phase.id)
      await handleReload({ phaseIds: [phase.id] })

      handleReloadOpenActivities?.()
      handleAddSuccessNotification('Notification.PhaseStartedKey')
    } catch {
      handleAddErrorNotification('Error.CommonApiRequestFailKey')
    } finally {
      setStartPhaseLoading(false)
    }
  }

  async function handleToggleVisibility() {
    const nextVisible = !phase.visible_to_tenant

    try {
      await togglePipelinePhaseVisibilityToTenant(phase.id)
      await handleReload({ phaseIds: [phase.id] })

      handleReloadOpenActivities?.()
      handleAddSuccessNotification(
        getActivityVisibilityChangeNotificationKey(nextVisible)
      )
    } catch {
      handleAddErrorNotification(
        getActivityVisibilityChangeNotificationKey(nextVisible, {
          isError: true,
        })
      )
    }
  }

  function handleAttachedThreadRead() {
    handleLoadCounters?.([REQUEST_PROFILE_TAB_EMAILS])
  }

  function renderThreadModal({ navigationContext, uuid, onClose, onReload }) {
    async function handleReloadAttachedThreadChanges() {
      onReload()

      return handleReloadAfterChange(REQUEST_PROFILE_CHANGE_TYPE.emails)
    }

    return (
      <RequestProfileTabEmailsThreadModal
        mailUuid={uuid}
        navigationContext={navigationContext}
        requestId={requestId}
        onClose={onClose}
        onReload={handleReloadAttachedThreadChanges}
        onSuccessRead={handleAttachedThreadRead}
      />
    )
  }

  function handleGoToAttachedEmails() {
    setModal(
      <EmailAttachedThreadsModal
        readonly
        attachedFilesProps={{
          scope: FILE_SCOPE_REQUEST,
          subject: requestId,
        }}
        entityId={requestId}
        getAttachedEmailThreads={getAttachedEmailThreads}
        getItemsFromResponse={getEmailThreadsFromResponse}
        headerProps={{ text: 'Modal.EmailAttachedThreadsTitleKey' }}
        loadItems={(_, params) =>
          getRequestEmailThreads(
            requestId,
            { ...params, phase: phase.id },
            { outboundUuid }
          )
        }
        renderThreadModal={renderThreadModal}
        onClose={closeModal}
      />
    )
  }

  function handleGoToAttachedFiles() {
    setModal(
      <FileAttachedItemsModal
        readonly
        ButtonCancel={false}
        ButtonRemove={false}
        entityId={phase.id}
        filePreviewProps={{
          scope: FILE_SCOPE_REQUEST,
          subject: requestId,
        }}
        getApiProps={getPhaseFilesApiProps}
        getData={params =>
          getPipelinePhaseFiles({ outboundUuid, params, phaseId: phase.id })
        }
        getDataPagination={getPhaseFilesPaginationData}
        getItemsFromResponse={getPhaseFilesFromResponse}
        onClose={closeModal}
      />
    )
  }

  return {
    handleAddActivity,
    handleGoToAttachedEmails,
    handleGoToAttachedFiles,
    handleStartPhase,
    handleToggleVisibility,
    isStartPhaseLoading,
  }
}
